/**
 * Shaping and scaling helpers behind SmartChart.
 *
 * The server decides *what* to plot (see build_chart_payload in Alex_main.py);
 * everything here is the mechanical half - grouping the rows that decision
 * implies, and working out axis bounds and tick formats that make the result
 * readable rather than technically correct.
 */

const OTHER_LABEL = 'Other';

/**
 * Numbers arrive from OData as "1,234.50" as often as as numbers.
 *
 * Only separators and a leading currency symbol are removed, and the rest has
 * to be a number outright: stripping every non-digit instead would quietly
 * turn a customer code like "CUST1" into the number 1, and a text column full
 * of those reads as a measure.
 */
export function parseNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const cleaned = String(value).trim().replace(/[\s,]/g, '').replace(/^[₹$€£¥]/, '');
  if (!/^[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?$/.test(cleaned)) return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseDate(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const str = String(value).trim();
  // "2025-07" is a month bucket, which Date.parse reads as UTC midnight - fine,
  // but it must not fall through to the generic parser and become a day.
  if (/^\d{4}-\d{2}$/.test(str)) return new Date(`${str}-01T00:00:00`);
  if (/^\d{4}$/.test(str)) return new Date(`${str}-01-01T00:00:00`);
  const parsed = new Date(str.replace(' ', 'T'));
  if (!Number.isNaN(parsed.getTime())) return parsed;
  const fallback = new Date(str);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

/** Tick text that matches how far apart the points actually are. */
export function makeDateFormatter(dates) {
  const times = dates.filter(Boolean).map((d) => d.getTime());
  if (!times.length) return (d) => String(d);
  const spanDays = (Math.max(...times) - Math.min(...times)) / 86400000;
  if (spanDays > 1500) {
    return (d) => String(d.getFullYear());
  }
  if (spanDays > 120) {
    return (d) => d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
  }
  if (spanDays > 2) {
    return (d) => d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  }
  return (d) => d.toLocaleString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Axis and tooltip formatting.
 *
 * Axis ticks are compact (12L, 3.4M) because a full rupee figure repeated down
 * an axis is what pushes the plot area into a sliver; the tooltip shows the
 * whole number, where there is room for it.
 */
export function makeValueFormatter(spec = {}) {
  const isCurrency = spec.value_format === 'currency' && spec.currency;
  const locale = spec.currency === 'INR' ? 'en-IN' : undefined;

  const compact = new Intl.NumberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: 1,
    ...(isCurrency ? { style: 'currency', currency: spec.currency, currencyDisplay: 'narrowSymbol' } : {}),
  });
  const full = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
    ...(isCurrency ? { style: 'currency', currency: spec.currency } : {}),
  });

  const guard = (formatter) => (value) => {
    const num = parseNumber(value);
    if (num === null) return '-';
    try {
      return formatter.format(num);
    } catch (e) {
      return String(num);
    }
  };

  return { compact: guard(compact), full: guard(full) };
}

/** Round a bound out to the next 1 / 2 / 2.5 / 5 x 10^n, so ticks land on readable numbers. */
function niceBound(value, direction) {
  if (value === 0) return 0;
  const sign = value < 0 ? -1 : 1;
  const magnitude = Math.abs(value);
  const exponent = Math.floor(Math.log10(magnitude));
  const base = Math.pow(10, exponent);
  const normalised = magnitude / base;
  const steps = [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10];
  const roundUp = (sign > 0) === (direction === 'up');
  let chosen;
  if (roundUp) {
    chosen = steps.find((step) => normalised <= step + 1e-9);
    if (chosen === undefined) chosen = 10;
  } else {
    const below = steps.filter((step) => step <= normalised + 1e-9);
    chosen = below.length ? below[below.length - 1] : 1;
  }
  return sign * chosen * base;
}

/**
 * Y-axis bounds.
 *
 * Bars are measured from zero - a truncated baseline turns a 3% difference
 * into a doubled bar. Lines are not: forcing zero onto a series that lives
 * between 980 and 1020 flattens the only thing worth seeing.
 */
export function computeDomain(values, { zeroBase = true } = {}) {
  const numbers = values.map(parseNumber).filter((v) => v !== null);
  if (!numbers.length) return [0, 1];

  let min = Math.min(...numbers);
  let max = Math.max(...numbers);
  if (min === max) {
    if (min === 0) return [0, 1];
    const pad = Math.abs(min) * 0.2;
    return zeroBase && min > 0 ? [0, niceBound(min + pad, 'up')] : [niceBound(min - pad, 'down'), niceBound(max + pad, 'up')];
  }

  if (zeroBase && min >= 0) return [0, niceBound(max, 'up')];
  if (zeroBase && max <= 0) return [niceBound(min, 'down'), 0];
  if (min < 0 && max > 0) return [niceBound(min, 'down'), niceBound(max, 'up')];

  const padding = (max - min) * 0.1;
  return [niceBound(min - padding, 'down'), niceBound(max + padding, 'up')];
}

/** A log axis only earns its place when the values really do span orders of magnitude. */
export function shouldUseLogScale(values) {
  const numbers = values.map(parseNumber).filter((v) => v !== null && v > 0);
  if (numbers.length < 4 || numbers.length !== values.length) return false;
  return Math.max(...numbers) / Math.min(...numbers) > 500;
}

function applyAggregation(bucket, agg) {
  if (!bucket || bucket.count === 0) return null;
  switch (agg) {
    case 'count':
      return bucket.count;
    case 'avg':
      return bucket.numericCount ? bucket.sum / bucket.numericCount : null;
    case 'min':
      return bucket.min;
    case 'max':
      return bucket.max;
    case 'none':
      return bucket.last;
    case 'sum':
    default:
      return bucket.sum;
  }
}

function emptyBucket() {
  return { sum: 0, count: 0, numericCount: 0, min: Infinity, max: -Infinity, last: null };
}

function pushValue(bucket, value) {
  bucket.count += 1;
  const num = parseNumber(value);
  if (num === null) return;
  bucket.numericCount += 1;
  bucket.sum += num;
  bucket.min = Math.min(bucket.min, num);
  bucket.max = Math.max(bucket.max, num);
  bucket.last = num;
}

/**
 * Group raw rows into the series the spec asks for.
 *
 * Returns chart-ready rows plus the series keys to plot, whether the x axis is
 * a date, and how many categories were folded into "Other".
 */
export function aggregateForSpec(rows, spec = {}) {
  const empty = { data: [], seriesKeys: [], xIsDate: false, otherCount: 0, xKey: 'x' };
  if (!Array.isArray(rows) || !rows.length || !spec || !spec.x) return empty;

  const { x, series, agg = 'sum', type } = spec;
  const measures = Array.isArray(spec.y) ? spec.y.filter(Boolean) : [];
  const isCount = agg === 'count' || (!measures.length && !series);

  // Scatter compares two measures row by row; grouping would destroy the point
  // of it.
  if (type === 'scatter') {
    const yCol = measures[0];
    const data = rows
      .map((row) => ({ [x]: parseNumber(row[x]), [yCol]: parseNumber(row[yCol]), __label: String(row[series] ?? '') }))
      .filter((row) => row[x] !== null && row[yCol] !== null);
    return { data, seriesKeys: [yCol], xIsDate: false, otherCount: 0, xKey: x };
  }

  const dateCandidates = rows.slice(0, 40).map((row) => parseDate(row[x]));
  const xIsDate = dateCandidates.filter(Boolean).length > dateCandidates.length * 0.7;

  const groups = new Map();
  rows.forEach((row) => {
    const rawX = row[x];
    const date = xIsDate ? parseDate(rawX) : null;
    if (xIsDate && !date) return;
    const key = xIsDate ? String(date.getTime()) : String(rawX ?? '');
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        date: xIsDate ? date : null,
        label: String(rawX ?? ''),
        sortKey: xIsDate ? date.getTime() : key,
        buckets: new Map(),
      });
    }
    const group = groups.get(key);

    if (isCount) {
      if (!group.buckets.has('Count')) group.buckets.set('Count', emptyBucket());
      pushValue(group.buckets.get('Count'), 1);
      return;
    }
    if (series) {
      const seriesKey = String(row[series] ?? 'Unknown');
      if (!group.buckets.has(seriesKey)) group.buckets.set(seriesKey, emptyBucket());
      pushValue(group.buckets.get(seriesKey), row[measures[0]]);
      return;
    }
    measures.forEach((measure) => {
      if (!group.buckets.has(measure)) group.buckets.set(measure, emptyBucket());
      pushValue(group.buckets.get(measure), row[measure]);
    });
  });

  const seriesKeySet = new Set();
  let assembled = Array.from(groups.values()).map((group) => {
    const entry = { __key: group.key, __sort: group.sortKey, __label: group.label, __date: group.date };
    group.buckets.forEach((bucket, key) => {
      const value = applyAggregation(bucket, isCount ? 'count' : agg);
      if (value !== null && Number.isFinite(value)) {
        entry[key] = value;
        seriesKeySet.add(key);
      }
    });
    entry.__total = Object.keys(entry)
      .filter((key) => !key.startsWith('__'))
      .reduce((sum, key) => sum + entry[key], 0);
    return entry;
  });

  // Cap the number of series so a stray high-cardinality column cannot paint
  // three hundred indistinguishable colours.
  let seriesKeys = Array.from(seriesKeySet);
  if (series && seriesKeys.length > 8) {
    const totals = new Map();
    assembled.forEach((entry) => {
      seriesKeys.forEach((key) => totals.set(key, (totals.get(key) || 0) + (entry[key] || 0)));
    });
    const kept = seriesKeys.sort((a, b) => (totals.get(b) || 0) - (totals.get(a) || 0)).slice(0, 8);
    const dropped = seriesKeys.filter((key) => !kept.includes(key));
    assembled = assembled.map((entry) => {
      const merged = { ...entry };
      let other = 0;
      dropped.forEach((key) => {
        other += merged[key] || 0;
        delete merged[key];
      });
      if (other) merged[OTHER_LABEL] = (merged[OTHER_LABEL] || 0) + other;
      return merged;
    });
    seriesKeys = withOtherSeries(kept, assembled);
  }

  const sort = spec.sort || (xIsDate ? 'x_asc' : 'value_desc');
  if (xIsDate || sort === 'x_asc' || sort === 'x_desc') {
    assembled.sort((a, b) => (a.__sort > b.__sort ? 1 : a.__sort < b.__sort ? -1 : 0));
    if (sort === 'x_desc') assembled.reverse();
  } else if (sort === 'value_asc') {
    assembled.sort((a, b) => a.__total - b.__total);
  } else if (sort !== 'none') {
    assembled.sort((a, b) => b.__total - a.__total);
  }

  // A line over time keeps every point; a bar chart of two hundred categories
  // is unreadable, so the tail becomes one honest "Other" bar.
  let otherCount = 0;
  const topN = Number(spec.top_n) || 12;
  const collapses = !xIsDate && type !== 'line' && type !== 'area';
  if (collapses && assembled.length > topN) {
    const head = assembled.slice(0, topN);
    const tail = assembled.slice(topN);
    otherCount = tail.length;
    const otherEntry = { __key: OTHER_LABEL, __sort: OTHER_LABEL, __label: `${OTHER_LABEL} (${otherCount})`, __total: 0 };
    tail.forEach((entry) => {
      seriesKeys.forEach((key) => {
        if (entry[key] !== undefined) otherEntry[key] = (otherEntry[key] || 0) + entry[key];
      });
      otherEntry.__total += entry.__total;
    });
    assembled = [...head, otherEntry];
  } else if (assembled.length > 400) {
    // Guard rail for a runaway line series.
    assembled = assembled.slice(0, 400);
  }

  // Date buckets only become tick text once the whole span is known - a single
  // day of orders wants times, three years of them want years.
  if (xIsDate) {
    const formatDate = makeDateFormatter(assembled.map((entry) => entry.__date));
    assembled.forEach((entry) => {
      if (entry.__date) entry.__label = formatDate(entry.__date);
    });
  }

  return { data: assembled, seriesKeys, xIsDate, otherCount, xKey: '__label' };
}

// Kept separate so the series-capping branch above reads in one line.
function withOtherSeries(kept, assembled) {
  const hasOther = assembled.some((entry) => entry[OTHER_LABEL] !== undefined);
  return hasOther ? [...kept, OTHER_LABEL] : kept;
}

/** Labels long enough to need rotating, or a horizontal chart entirely. */
export function labelMetrics(data, xKey) {
  const labels = data.map((entry) => {
    const value = entry[xKey] ?? entry.__label;
    return value instanceof Date ? '' : String(value ?? '');
  });
  const longest = labels.reduce((max, label) => Math.max(max, label.length), 0);
  return { count: labels.length, longest };
}

export { OTHER_LABEL };

/**
 * Client-side stand-in for the server's column profiling.
 *
 * Used only for messages that predate the chart spec (they are replayed from
 * IndexedDB with rows but no spec) and as a guard if the server payload is
 * missing one. The rules mirror profile_columns in Alex_main.py, minus the
 * declared schema it has and the browser does not.
 */
const ID_TAIL_TOKENS = new Set(['id', 'ids', 'uuid', 'guid', 'code', 'key', 'no', 'num',
  'number', 'sku', 'ean', 'gtin', 'ref', 'item']);
const MEASURE_TOKENS = new Set(['amount', 'amt', 'value', 'price', 'cost', 'revenue', 'net',
  'gross', 'total', 'sum', 'spend', 'balance', 'credit', 'debit', 'quantity', 'qty',
  'count', 'weight', 'volume', 'percent', 'percentage', 'rate', 'margin', 'profit', 'days']);
const PERIOD_TOKENS = new Set(['year', 'fiscalyear', 'period', 'month', 'quarter', 'week']);
// SAP keys that are numeric and mean nothing when summed. Mirrors
// _ID_FULL_NAMES in Alex_main.py.
const ID_FULL_NAMES = new Set(['salesorder', 'salesorderitem', 'purchaseorder', 'purchaseorderitem',
  'purchaserequisition', 'purchaserequisitionitem', 'material', 'plant', 'companycode', 'customer',
  'supplier', 'vendor', 'batch', 'billingdocument', 'deliverydocument', 'accountingdocument',
  'materialdocument', 'costcenter', 'glaccount', 'profitcenter', 'documentnumber',
  'referencedocument', 'soldtoparty', 'shiptoparty', 'billtoparty', 'payerparty']);

function splitName(name) {
  return (String(name).match(/[A-Z]+(?![a-z])|[A-Z][a-z]+|[a-z]+|\d+/g) || []).map((t) => t.toLowerCase());
}

export function inferColumnMeta(rows) {
  if (!Array.isArray(rows) || !rows.length) return [];
  return Object.keys(rows[0]).map((name) => {
    const values = rows.map((row) => row[name]).filter((v) => v !== null && v !== undefined && v !== '');
    const tokens = splitName(name);
    const tokenSet = new Set(tokens);
    const unique = new Set(values.map(String)).size;

    const dateRatio = values.length ? values.filter((v) => parseDate(v) && /[-/:]/.test(String(v))).length / values.length : 0;
    const numericRatio = values.length ? values.filter((v) => parseNumber(v) !== null).length / values.length : 0;

    let role;
    if (dateRatio > 0.7 && numericRatio < 0.7) {
      role = 'temporal';
    } else if ([...tokenSet].some((t) => PERIOD_TOKENS.has(t))) {
      role = 'dimension';
    } else if (numericRatio > 0.7) {
      const isMeasureName = tokens.some((t) => MEASURE_TOKENS.has(t));
      const looksLikeKey = !isMeasureName
        && (ID_FULL_NAMES.has(tokens.join(''))
          || ID_TAIL_TOKENS.has(tokens[tokens.length - 1])
          || (values.length >= 8 && unique / values.length > 0.95
              && values.every((v) => Number.isInteger(parseNumber(v)))));
      role = looksLikeKey ? 'dimension' : 'measure';
    } else {
      role = 'dimension';
    }
    return { name, role, unique, ordered: role === 'temporal' || [...tokenSet].some((t) => PERIOD_TOKENS.has(t)) };
  });
}

/** The deterministic pick, mirroring heuristic_chart_spec on the server. */
export function inferSpec(rows, meta) {
  const columns = meta && meta.length ? meta : inferColumnMeta(rows);
  const rowCount = Array.isArray(rows) ? rows.length : 0;
  const measures = columns.filter((c) => c.role === 'measure');
  const temporals = columns.filter((c) => c.role === 'temporal');
  const dimensions = columns.filter((c) => c.role === 'dimension');

  if (!rowCount) return { type: 'table' };
  if (rowCount === 1) {
    return measures.length
      ? { type: 'kpi', y: measures.slice(0, 4).map((c) => c.name), agg: 'none' }
      : { type: 'table' };
  }
  if (temporals.length && measures.length) {
    return {
      type: 'line', x: temporals[0].name, y: measures.slice(0, 2).map((c) => c.name),
      agg: 'sum', sort: 'x_asc', top_n: 60,
    };
  }
  if (dimensions.length && measures.length) {
    const byCardinality = [...dimensions].sort((a, b) => a.unique - b.unique);
    const x = byCardinality[0];
    const series = byCardinality.slice(1).find((c) => c.unique > 1 && c.unique <= 6);
    return {
      type: series ? 'grouped_bar' : 'bar',
      x: x.name,
      y: [measures[0].name],
      series: series ? series.name : null,
      agg: 'sum',
      sort: x.ordered ? 'x_asc' : 'value_desc',
      top_n: 12,
    };
  }
  if (dimensions.length) {
    return { type: 'bar', x: dimensions[0].name, y: [], agg: 'count', sort: 'value_desc', top_n: 12 };
  }
  if (measures.length >= 2) {
    return { type: 'scatter', x: measures[0].name, y: [measures[1].name], agg: 'none' };
  }
  if (measures.length) {
    return { type: 'kpi', y: [measures[0].name], agg: 'sum' };
  }
  return { type: 'table' };
}
